import { useMemo } from 'react';

import { RowData } from '@tanstack/react-table';
import { useAtomValue } from 'jotai';
import { ActionMenu, Card, Center, NoTableData, ScrollArea, rem, useMantineTheme } from 'ui';
import { useElementSize } from 'ui/Hooks';

import { ErrorBoundary } from '../../src/components/molecules/error-boundary/ErrorBoundary';
import { ColumnPinningState } from '../../src/hooks/tables';
import classes from './Table.module.css';
import { TableBody } from './TableContent/TableBody/TableBody';
import { TableContent } from './TableContent/TableContent';
import { TableHead } from './TableContent/TableHead/TableHead';
import { TableRowAction } from './TableContent/TableRowAction';
import { TableDataProviderComponent } from './TableDataProvider';
import { TableHeader } from './TableHeader/TableHeader';
import { TablePagination } from './TablePagination/TablePagination';
import { TableRoot } from './TableRoot/TableRoot';
import { TableSearchBadges } from './TableSearchBadges';
import { ACTIONS_MENU_COLUMN_ID } from './config/constants';
import { useTableContext } from './table.context';
import type { TableProps } from './types/types';

const NoDataComponent = ({ component }: { component?: React.ReactNode | string }) => {
  const ctx = useTableContext();
  const dataLength = useMemo(() => ctx.data.length, [ctx.data.length]);
  const searchObject = useAtomValue(ctx.tableSearchObjectAtom, {
    store: ctx.atomStore,
  });

  const hasSearch = useMemo(() => {
    return searchObject.searches && Object.entries(searchObject.searches).length > 0;
  }, [searchObject.searches]);

  if (ctx.isLoading !== false) return null;
  if (dataLength > 0) return null;
  return (
    <>
      {component && typeof component !== 'string' ? (
        <Center>{component}</Center>
      ) : (
        <NoTableData text={component as string} hasSearch={hasSearch} />
      )}
    </>
  );
};

/** Helper fn to ensure some Column pinning state exists since there is so many optionals */
const getColumnPinningState = (columnPinning: ColumnPinningState = {}) => {
  const right = columnPinning?.right ?? [];
  const state = {
    right: right,
  };
  if (columnPinning?.left) state['left'] = columnPinning.left;

  return state;
};

const Table = <
  TData extends RowData,
  TMeta extends Record<string, unknown> = Record<string, never>,
>({
  title,
  children,
  withPagination = true,
  withBorder,
  withShadow = true,
  noDataComponent,
  initialState,
  tableContainerProps,
  uiTableProps,
  stickyHeader = true,
  headerProps,
  pinActions = true,
  reactTableOptions,
  withSearchParams = true,
  ...others
}: TableProps<TData, TMeta>) => {
  const theme = useMantineTheme();
  const { width, ref } = useElementSize();

  /** Handle any needed mutations for initial State  */
  const _initialState = useMemo(() => {
    const { columnPinning } = initialState ?? {};

    const handleColumnPinning = () => {
      const _columnPinning = getColumnPinningState(columnPinning);
      let { right } = _columnPinning;
      // If pinActions is true, Ensure actions is the last column in the right array
      if (pinActions) {
        right = right.filter((col) => col !== 'actions');
        right.push('actions');
        right.push(ACTIONS_MENU_COLUMN_ID);
      }
      Object.assign(_columnPinning.right, right);
      return _columnPinning;
    };

    const state = {
      ...initialState,
      ...reactTableOptions?.initialState,
      columnPinning: handleColumnPinning(),
    };

    return state;
  }, [initialState, reactTableOptions?.initialState, pinActions]);

  return (
    <ErrorBoundary.Container>
      <TableRoot {...reactTableOptions} initialState={_initialState}>
        {/* Table Main Container */}
        {withSearchParams ? <TableSearchBadges /> : null}
        <Card
          bg="white"
          withBorder={withBorder}
          style={{
            padding: 0,
            borderRadius: theme.radius.sm,
            boxShadow: withShadow ? theme.shadows.md : 'none',
            // if border, style should be set as below, not with default
            border: withBorder ? `1px solid ${theme?.colors.gray[1]}` : undefined,
            overflow: 'visible',
            width: rem(tableContainerProps?.maw) ?? rem(tableContainerProps?.w) ?? 'auto',
          }}
        >
          <ErrorBoundary.Container>
            <TableHeader
              title={title}
              searchFields={headerProps?.searchFields}
              exportable={headerProps?.exportable}
              header={headerProps?.header}
              withSearchParams={withSearchParams}
              {...headerProps}
            />

            <Card {...tableContainerProps} p={0} ref={ref} radius="sm" shadow="none">
              <ScrollArea.Autosize
                // Experimenting with using variables to pass down to the scroll area component.
                style={{ '--parent-width': `${width}px` }}
                classNames={{
                  root: classes.scrollAreaRoot,
                  scrollbar: classes.scrollAreaScrollbar,
                }}
                mah={tableContainerProps?.mah ?? tableContainerProps?.h}
              >
                <TableContent {...others} stickyHeader={stickyHeader} {...uiTableProps} />
              </ScrollArea.Autosize>

              <NoDataComponent component={noDataComponent} />
            </Card>
          </ErrorBoundary.Container>
        </Card>
        <TablePagination withPagination={withPagination} />
        {children}
      </TableRoot>
    </ErrorBoundary.Container>
  );
};

Table.Root = TableRoot;
Table.Header = TableHeader;
Table.Content = TableContent;
Table.Head = TableHead;
Table.Body = TableBody;
Table.RowAction = TableRowAction;

Table.Data = TableDataProviderComponent;
Table.Pagination = TablePagination;
Table.useContext = useTableContext;
Table.ActionMenu = ActionMenu;
export { Table };
